import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { NEUNEU_TOUR_END_EVENT, NEUNEU_TOUR_START_EVENT } from './NeuNeuAI/neuNeuTour';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { DarkBackground } from './DarkBackground';
import { NeuNeuWidget } from './NeuNeuAI/NeuNeuWidget';

export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const tourOpenedDrawer = useRef(false);
  const loc = useLocation();

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Reset scroll state on route change
  useEffect(() => { setScrolled(false); }, [loc.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [drawerOpen]);

  // Open mobile nav drawer for NeuNeu site tour (nav targets live in the drawer below lg)
  useEffect(() => {
    const onTourStart = () => {
      tourOpenedDrawer.current = true;
      setDrawerOpen(true);
    };
    const onTourEnd = () => {
      if (tourOpenedDrawer.current) {
        setDrawerOpen(false);
        tourOpenedDrawer.current = false;
      }
    };
    window.addEventListener(NEUNEU_TOUR_START_EVENT, onTourStart);
    window.addEventListener(NEUNEU_TOUR_END_EVENT, onTourEnd);
    return () => {
      window.removeEventListener(NEUNEU_TOUR_START_EVENT, onTourStart);
      window.removeEventListener(NEUNEU_TOUR_END_EVENT, onTourEnd);
    };
  }, []);

  // Track scroll on the main container
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 8);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-1">
      <div className="dark:block hidden">
        <DarkBackground variant="page" />
      </div>
      <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header onMenuClick={() => setDrawerOpen(true)} scrolled={scrolled} />
        <main ref={mainRef} className="relative z-10 flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
      {/* Mobile FAB — desktop NeuNeu lives at the bottom of the sidebar */}
      <NeuNeuWidget placement="fab" />
    </div>
  );
}
